import React from "react";
import {
  bookingLabel,
  money,
  orderDate,
  paymentLabel,
  progressIndex,
  statusLabel,
} from "./helpers";

const serviceIcon = (service = "") => {
  const value = service.toLowerCase();
  if (value.includes("iron")) return "fa-bolt";
  if (value.includes("dry")) return "fa-wind";
  if (value.includes("cap")) return "fa-hat-cowboy";
  return "fa-shirt";
};

export const PageHeading = ({ title, subtitle, details }) => (
  <div className="odr-heading">
    <div><h1>{title}</h1><p>{subtitle}</p></div>
    <span className={`odr-status odr-status-${details.order_status || "pending"}`}>
      <i className="fa-regular fa-calendar-check" />
      {statusLabel(details.order_status)}
    </span>
  </div>
);

export const OrderOverview = ({ details }) => (
  <section className="odr-card odr-overview">
    <div><small>Order ID</small><strong>{details.id}</strong></div>
    <div>
      <small>Booked On</small>
      <strong>{orderDate(details.creation_date, "eee, dd MMM yyyy")}</strong>
      <span>{orderDate(details.creation_date, "p")}</span>
    </div>
    <div><small>Order Type</small><strong className="odr-pill yellow">{bookingLabel(details)}</strong></div>
    <div><small>Payment Method</small><strong className="odr-pill green">{paymentLabel(details.payment_method)}</strong></div>
    <div><small>Total Amount</small><strong className="odr-total">Rs. {money(details.order_amount)}</strong></div>
  </section>
);

export const OrderProgress = ({ details, onOpenTracking }) => {
  const progress = progressIndex(details);
  const stages = [
    { label: "Booked", icon: "fa-clipboard-check", date: details.pending || details.creation_date },
    { label: "Pickup Scheduled", icon: "fa-calendar-day", date: details.pickup_date, time: details.pickup_time, dateOnly: true },
    { label: "In Process", icon: "fa-soap", date: details.processing },
    { label: "Out for Delivery", icon: "fa-truck", date: details.out_for_delivery },
    { label: "Delivered", icon: "fa-circle-check", date: details.delivered },
  ];
  return (
    <button className="odr-card odr-progress" type="button" onClick={onOpenTracking}>
      <span className="odr-progress-line" />
      {stages.map((stage, index) => (
        <span className={`odr-stage ${index < progress ? "active" : ""} ${index === progress - 1 ? "current" : ""}`} key={stage.label}>
          <span className="odr-stage-icon"><i className={`fa-solid ${stage.icon}`} /></span>
          <strong>{stage.label}</strong>
          {stage.date && <small>{orderDate(stage.date, "eee, dd MMM yyyy", stage.dateOnly)}</small>}
          {stage.time && <small>{stage.time}</small>}
        </span>
      ))}
    </button>
  );
};

export const ServiceAccordions = ({ services, open, onToggle }) => (
  <div className="odr-services">
    {services.map((service, serviceIndex) => {
      const expanded = Boolean(open[service.services_id]);
      return (
        <section className="odr-card odr-service" key={service.services_id}>
          <button type="button" className="odr-service-header" onClick={() => onToggle(service.services_id)} aria-expanded={expanded}>
            <span className={`odr-icon tone-${serviceIndex % 4}`}><i className={`fa-solid ${serviceIcon(service.service)}`} /></span>
            <strong>{service.service}</strong>
            <i className={`fa-solid fa-chevron-${expanded ? "up" : "down"}`} />
          </button>
          {expanded && (
            <div className="odr-items">
              {service.items.map((item, itemIndex) => (
                <div className="odr-item" key={`${item.laundry_item_id}-${itemIndex}`}>
                  <div>
                    <span>{item.quantity} x {item.name}</span>
                    {item.item_note && <small>Note: {item.item_note}</small>}
                    {item.addons?.map((addon, index) => <small key={`${addon.name}-${index}`}>{addon.qty} x {addon.name}</small>)}
                    {item.is_bundle_active && <em><i className="fa-solid fa-wand-magic-sparkles" />Bundle offer items included.</em>}
                  </div>
                  <strong>Rs. {money(item.price * (item.quantity - (item.free_items || 0)))}</strong>
                </div>
              ))}
            </div>
          )}
        </section>
      );
    })}
  </div>
);

const SideHeader = ({ icon, tone = "green", children }) => (
  <header><span className={`odr-icon ${tone}`}><i className={icon} /></span><h3>{children}</h3></header>
);

export const PriceSummary = ({ count, itemPrice, details }) => (
  <section className="odr-card odr-side-card">
    <SideHeader icon="fa-solid fa-receipt">Price Summary</SideHeader>
    <div className="odr-price-lines">
      <div><span>Total Items</span><strong>{count} item(s)</strong></div>
      <div><span>Item Price</span><strong>Rs. {money(itemPrice)}</strong></div>
      {Number(details.referral_discount_amount) > 0 && <div className="discount"><span>Referral Discount</span><strong>−Rs. {money(details.referral_discount_amount)}</strong></div>}
      {Number(details.discount_amount) > 0 && <div className="discount"><span>Discount</span><strong>−Rs. {money(details.discount_amount)}</strong></div>}
      {details.coupon_code && <div><span>Voucher Used</span><strong>{details.coupon_code}</strong></div>}
      <hr />
      <div><span>Subtotal</span><strong>Rs. {money(details.subtotal)}</strong></div>
      <div><span>VAT / Tax</span><strong>Rs. {money(details.tax_amount)}</strong></div>
      <div><span>Delivery Fee</span><strong>Rs. {money(details.delivery_charge)}</strong></div>
    </div>
    <footer><strong>Total</strong><strong>Rs. {money(details.order_amount)}</strong></footer>
  </section>
);

export const ScheduledDates = ({ details }) => (
  <section className="odr-card odr-side-card">
    <SideHeader icon="fa-regular fa-calendar">Scheduled Dates</SideHeader>
    <div className="odr-schedule">
      <div><span className="odr-icon yellow"><i className="fa-solid fa-arrow-up" /></span><p><small>Pickup</small><strong>{orderDate(details.pickup_date, "eee, dd MMM", true)}</strong><span>{details.pickup_time || "—"}</span></p></div>
      {details.delivery_date && <div><span className="odr-icon green"><i className="fa-solid fa-arrow-down" /></span><p><small>Delivery</small><strong>{orderDate(details.delivery_date, "eee, dd MMM", true)}</strong><span>{details.order_delivery_time || "—"}</span></p></div>}
    </div>
  </section>
);

export const OrderNotes = ({ notes }) => notes.length ? (
  <section className="odr-card odr-side-card">
    <SideHeader icon="fa-solid fa-note-sticky" tone="yellow">Order Note</SideHeader>
    <ul className="odr-notes">{notes.map((entry, index) => <li key={entry.id ?? index}>{entry.note ?? String(entry)}</li>)}</ul>
  </section>
) : null;

export const RiderDetails = ({ rider, imageBaseUrl }) => rider ? (
  <section className="odr-card odr-side-card odr-rider-card">
    <SideHeader icon="fa-solid fa-motorcycle">Rider Details</SideHeader>
    <div className="odr-rider"><img src={`${imageBaseUrl}/${rider.image}`} alt="" /><strong>{rider.f_name} {rider.l_name}</strong><a href={`tel:${rider.phone}`} aria-label="Call rider"><i className="fa-solid fa-phone" /></a></div>
  </section>
) : null;

export const HelpCard = () => (
  <section className="odr-card odr-help">
    <span className="odr-icon green"><i className="fa-solid fa-headphones" /></span>
    <div><strong>Need help with your order?</strong><small>We're here to help with any queries.</small></div>
    <a href="mailto:support@jabchaho.com"><i className="fa-solid fa-headphones" />Contact Support</a>
  </section>
);
